import { importEDocuments } from "@/api/EDocumentImportApi";
import { ImportProgress, ImportSummary } from "@utils/import/core/types";
import { buildEDocumentPayload } from "./buildEDocumentPayload";
import { normalizeIdentification } from "./loadEDocumentImportContext";
import { applyCreatedClients, collectPendingClients, createMissingClients } from "./resolveEDocuments";
import { EDocumentImportContext, EDocumentImportDefinition, ResolvedEDocument } from "./types";

export type CommitEDocumentImportResult = {
    summary: ImportSummary;
    /** Documentos que no se enviaron por duplicados o errores. */
    skippedCount: number;
    createdClientsCount: number;
    /**
     * Documentos con los clientes recien creados ya ligados. El asistente los guarda para que
     * un reintento tras un fallo no vuelva a crear los mismos clientes.
     */
    documents: ResolvedEDocument[];
};

/**
 * Ejecuta la carga: crea los clientes que faltan, arma el payload y manda los lotes.
 *
 * Los clientes se crean antes de enviar para que la lambda reciba `clientId` ya resueltos y
 * para que `ClientApi` invalide su cache; son unos pocos, no cientos. Mismo orden que la
 * importacion de CSV con las zonas y las categorias.
 *
 * Si el envio falla a medias, los clientes creados quedan registrados en el contexto y en los
 * documentos devueltos: reintentar no los duplica. Los comprobantes tampoco se duplican, porque
 * la lambda deriva su id de la clave de Hacienda.
 */
export const commitEDocumentImport = async (params: {
    documents: ResolvedEDocument[];
    context: EDocumentImportContext;
    definition: EDocumentImportDefinition;
    onProgress?: (progress: ImportProgress) => void;
}): Promise<CommitEDocumentImportResult> => {
    const { documents, context, definition, onProgress } = params;

    const pendingClients = collectPendingClients(documents);
    const knownIds = new Set(context.clients.map((client) => client.id));
    const resolvedClients = await createMissingClients(pendingClients, context.clients);

    // El contexto se actualiza de inmediato, antes de cualquier envio que pueda fallar, para
    // que un reintento reutilice estos clientes en vez de crearlos otra vez.
    const createdClients = resolvedClients.filter((client) => !knownIds.has(client.id));
    createdClients.forEach((client) => {
        const identification = normalizeIdentification(client.identificacion);
        context.clients.push(client);
        if (identification) context.clientsByIdentification.set(identification, client);
    });

    const linked = applyCreatedClients(documents, resolvedClients);
    const rows = buildEDocumentPayload(linked, definition);

    const summary = await importEDocuments(definition.documentType, rows, onProgress);

    await definition.invalidateCaches();

    return {
        summary,
        skippedCount: documents.length - rows.length,
        createdClientsCount: createdClients.length,
        documents: linked,
    };
};
